import React, { useState, useEffect, useContext } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { ErrorAlert, Label } from 'smartsuite-ui';
import LandingPagePanelSelector from './LandingPagePanelSelector';
import EditRemoveMenu from './EditRemoveMenu';
import TextOrLinkTitle from '../shared/TextOrLinkTitle';
import {
    getChartRenderProps,
    getGridStyles,
} from '../viewDashboards/dashboardUtils';
import {
    formatLandingPageWidgetMetricRequests,
    getTimeZoneFilterProperties,
} from '../viewDashboards/utils/metricRequestUtils';
import DashboardContext from '../shared/hooks/dashboardContext';
import { getLegendFormatter } from '../viewDashboards/widgets/utils/dataFormatUtils';

import { getThresholdsWithValues } from '../viewDashboards/utils/configurationAndValidationUtils';
import { useLabelContext } from '../shared/hooks/labelContext';
import { SAVED_DASHBOARD_NAME } from '../viewDashboards/dashboardLinkUtils';
import { useModal, useTick } from '../shared/hooks/dashboardHooks';

import ConfirmModal from '../viewDashboards/sharingAndSaving/ConfirmModal';
import { systemWidgetType } from './landingPageConstants';
import {
    handleErrorResult,
    metricDataPromise,
} from '../viewDashboards/utils/fetchUtils';
import WidgetLoading from '../shared/WidgetLoading';
import useLink from '../shared/hooks/linkHooks';

function getWidgetClass(hasChart = true) {
    let className = 'landing-page-widget widget';

    if (hasChart) {
        className += ' widget--with-chart';
    }

    return className;
}

/* eslint-disable complexity */
function buildPanelTitle(
    dashboardLabels,
    displayViewTitle,
    panelTitleOverride,
    dashboardTitle,
    viewKey,
    savedFilterDescription
) {
    if (panelTitleOverride) {
        return dashboardLabels[panelTitleOverride] ?? panelTitleOverride;
    }

    let title = `${dashboardTitle}`;
    if (displayViewTitle) {
        title += dashboardLabels[viewKey]
            ? `: ${dashboardLabels[viewKey]}`
            : `: ${viewKey}`;
    }

    title += savedFilterDescription ? ` - ${savedFilterDescription}` : '';

    return title;
}

function buildPanelLink(
    uriConstants,
    panelTitleOverride,
    dashboardKey,
    viewKey,
    savedFilterKey
) {
    if (panelTitleOverride) {
        return null;
    }

    let dashboardLink = `Dashboard/${dashboardKey}`;
    if (viewKey) {
        dashboardLink += `/${viewKey}`;
    }
    if (savedFilterKey) {
        dashboardLink += `?${SAVED_DASHBOARD_NAME}=${savedFilterKey}`;
    }

    return uriConstants.getShareableUri(dashboardLink);
}

function LandingPagePanel(props) {
    const { dashboardLabels, translationLabels } = useLabelContext();
    const { canModifyLandingPage, thresholdValues, uriConstants } =
        useContext(DashboardContext);

    const [isEditModalVisible, closeEditModal, showEditModal] = useModal();
    const [
        isConfirmDeleteModalVisible,
        closeConfirmDeleteModal,
        showConfirmDeleteModal,
    ] = useModal();

    const [metricData, setMetricData] = useState([]);
    const [error, setError] = useState(null);
    const [downloadData, setDownloadData] = useState();
    const [tick] = useTick(props.refreshRateMilliseconds);

    const linkUri = useLink(props.landingPageWidget.linkConfig);

    function callData() {
        if (props.landingPageWidget.type === systemWidgetType) {
            return null;
        }

        if (!props.landingPageWidget.metricRequests.length) {
            return null;
        }

        const requestObject = {
            dashboardKey: props.dashboardKey,
            viewKey: props.viewKey,
            metricRequests: formatLandingPageWidgetMetricRequests(
                props.landingPageWidget
            ),
            ...getTimeZoneFilterProperties(
                props.landingPageWidget.timeAndPrecision.timeZoneFiltering,
                props.landingPageWidget.filterValues
            ),
        };

        return metricDataPromise(
            uriConstants.metricDataUri,
            requestObject
        ).then(
            (response) => {
                setMetricData(response);
                setError(null);
            },
            (err) => handleErrorResult(err, setError)
        );
    }

    /* eslint-disable react-hooks/exhaustive-deps */
    useEffect(() => {
        if (tick) {
            callData();
        }
    }, [tick]);

    useEffect(() => {
        setMetricData([]);
        callData();
    }, [props.landingPageWidget]);
    /* eslint-enable react-hooks/exhaustive-deps */

    if (error) {
        const message =
            error.exceptionMessage ?? error.message ?? error.toString();

        return (
            <div
                className="grid-panel"
                style={getGridStyles(props.panelLayout.position)}
            >
                <ErrorAlert>{message}</ErrorAlert>
            </div>
        );
    }

    function renderWidgetTitle() {
        if (!props.landingPageWidget.title) {
            return null;
        }

        return (
            <div
                className="widget__header"
                style={props.landingPageWidget.headerStyles}
            >
                <div className="title title--size-small widget__header-text">
                    <span>{props.landingPageWidget.title}</span>
                </div>
            </div>
        );
    }

    function renderChart() {
        if (props.landingPageWidget.type === systemWidgetType) {
            return props.landingPageWidget.configuration.render({
                openLinksInNewTab: props.openLinksInNewTab,
            });
        }

        if (_.isEmpty(metricData)) {
            return <WidgetLoading />;
        }

        const legendFormatter = getLegendFormatter(
            props.landingPageWidget.friendlyLegendLabels,
            props.friendlyColumnLabels
        );

        const thresholdsWithValues = getThresholdsWithValues(
            props.landingPageWidget.thresholds,
            thresholdValues,
            props.landingPageWidget.filterValues
        );

        return props.landingPageWidget.configuration.render(
            getChartRenderProps(
                props.landingPageWidget,
                props.landingPageWidget.metricRequests,
                metricData,
                thresholdsWithValues,
                legendFormatter,
                dashboardLabels,
                props.openLinksInNewTab,
                setDownloadData
            )
        );
    }

    const navigationLink = props.landingPageWidget.linkConfig
        ? linkUri
        : buildPanelLink(
            uriConstants,
            props.landingPageWidget.panelTitleOverride,
            props.dashboardKey,
            props.viewKey,
            props.savedFilterKey
        );

    return (
        <div
            className={props.panelClassName}
            style={getGridStyles(props.panelLayout.position)}
        >
            <div
                className="title title--size-medium grid-panel__header-text"
                style={props.panelLayout.headerStyles}
            >
                <TextOrLinkTitle
                    navigationLink={navigationLink}
                    openLinkInNewTab={props.openLinksInNewTab}
                    title={buildPanelTitle(
                        dashboardLabels,
                        props.displayViewTitle,
                        props.landingPageWidget.panelTitleOverride,
                        props.dashboardTitle,
                        props.viewKey,
                        props.savedFilterDescription
                    )}
                />
                {canModifyLandingPage && (
                    <EditRemoveMenu
                        deletePanel={showConfirmDeleteModal}
                        editPanel={() => {
                            showEditModal();
                        }}
                        onDownload={() =>
                            props.onDownload(
                                downloadData,
                                props.landingPageWidget.title
                            )
                        }
                        showDownload={!!downloadData}
                    />
                )}
            </div>
            <div className="grid-panel__widgets">
                <div
                    className={getWidgetClass(
                        props.landingPageWidget.configuration.hasChart
                    )}
                >
                    {renderWidgetTitle()}
                    <div
                        className="widget-body"
                        style={props.landingPageWidget.bodyStyles}
                    >
                        {renderChart()}
                    </div>
                </div>
            </div>
            {isEditModalVisible && (
                <LandingPagePanelSelector
                    closeModal={() => {
                        closeEditModal();
                    }}
                    configurationKey={props.landingPageWidget.configurationKey}
                    modalHeader={dashboardLabels.editWidget}
                    onDelete={() => {
                        closeEditModal();
                        showConfirmDeleteModal();
                    }}
                    onDownload={props.onDownload}
                    onSave={(panel) => {
                        props.handleEdit(panel);
                    }}
                    panelIndex={props.panelIndex}
                    dashboardKey={props.dashboardKey}
                    viewKey={props.viewKey}
                    savedFilterKey={props.savedFilterKey}
                    landingPageWidgetId={props.landingPageWidgetId}
                />
            )}
            {isConfirmDeleteModalVisible && (
                <ConfirmModal
                    confirmTitle={translationLabels.confirm}
                    confirmLabel={translationLabels.yes}
                    dismissLabel={translationLabels.no}
                    onConfirm={() => {
                        props.handleDelete(props.panelIndex);
                    }}
                    onDismiss={closeConfirmDeleteModal}
                >
                    <Label>{dashboardLabels.confirmLandingPageDelete}</Label>
                </ConfirmModal>
            )}
        </div>
    );
}
/* eslint-enable complexity */

LandingPagePanel.propTypes = {
    dashboardKey: PropTypes.string,
    dashboardTitle: PropTypes.string,
    displayViewTitle: PropTypes.bool,
    friendlyColumnLabels: PropTypes.instanceOf(Object),
    handleDelete: PropTypes.func.isRequired,
    handleEdit: PropTypes.func.isRequired,
    landingPageWidget: PropTypes.instanceOf(Object).isRequired,
    landingPageWidgetId: PropTypes.string.isRequired,
    onDownload: PropTypes.func.isRequired,
    openLinksInNewTab: PropTypes.bool.isRequired,
    panelClassName: PropTypes.string.isRequired,
    panelLayout: PropTypes.instanceOf(Object).isRequired,
    panelIndex: PropTypes.number.isRequired,
    refreshRateMilliseconds: PropTypes.number,
    savedFilterKey: PropTypes.string,
    savedFilterDescription: PropTypes.string,
    viewKey: PropTypes.string,
};

LandingPagePanel.defaultProps = {
    displayViewTitle: false,
    friendlyColumnLabels: {},
};

export default LandingPagePanel;
